import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, ExternalLink, Scale, ChevronRight, Wallet, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { candidatiRegionali, candidatiComunali, elezioni2025, trasparenzaLegge, siteInfo, versamenti } from '../data/siteData';

// Helper to format currency
const formatCurrency = (amount) => {
  return amount.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const ElezioniTrasparenti = () => {
  // Memoize expensive calculations
  const versamentiWithTotals = useMemo(() => {
    return versamenti.map(periodo => ({
      ...periodo,
      totale: periodo.contributi.reduce((sum, c) => sum + c.importo, 0)
    }));
  }, []);

  const totaleGenerale = useMemo(() => {
    return versamentiWithTotals.reduce((total, periodo) => total + periodo.totale, 0);
  }, [versamentiWithTotals]);

  return (
    <div className="min-h-screen pt-20" data-testid="elezioni-trasparenti-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#002060] to-[#003380] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla home
          </Link>
          
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Scale className="w-6 h-6 text-[#FFD700]" />
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5CB9E5]">
                Trasparenza
              </span>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl font-bold text-white mb-6">
              Elezioni Trasparenti
            </h1>
            <p className="text-white/80 max-w-3xl mx-auto text-lg">
              In ottemperanza alla legge sulla trasparenza dei partiti e movimenti politici
            </p>
          </div>
        </div>
      </section>

      {/* Legge Info */}
      <section className="py-12 bg-[#F4F6F8]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm border-l-4 border-[#5CB9E5]">
            <h2 className="font-serif text-xl font-bold text-[#002060] mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#5CB9E5]" />
              Riferimento normativo
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">
              {trasparenzaLegge}
            </p>
          </div>
        </div>
      </section>

      {/* Sezione Versamenti */}
      <section className="py-16 bg-white" data-testid="versamenti-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Wallet className="w-6 h-6 text-[#FFD700]" />
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5CB9E5]">
                Trasparenza Finanziaria
              </span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#002060] mb-4">
              Versamenti e Contributi
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Elenco dei versamenti effettuati a sostegno del movimento
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {versamentiWithTotals.map((periodo) => (
              <div 
                key={periodo.mese}
                className="bg-[#F4F6F8] rounded-xl overflow-hidden"
                data-testid={`versamenti-${periodo.mese.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="bg-[#002060] px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-[#FFD700]" />
                    <h3 className="font-serif text-xl font-bold text-white">
                      {periodo.mese}
                    </h3>
                  </div>
                  <span className="text-[#FFD700] font-semibold">
                    Totale: €{formatCurrency(periodo.totale)}
                  </span>
                </div>
                <div className="p-4">
                  <table className="w-full">
                    <tbody>
                      {periodo.contributi.map((contributo, idx) => (
                        <tr 
                          key={`${periodo.mese}-${contributo.nome}-${idx}`}
                          className="border-b border-slate-200 last:border-0"
                        >
                          <td className="py-3 text-[#002060] font-medium">
                            {contributo.nome}
                          </td>
                          <td className="py-3 text-right text-[#002060] font-semibold">
                            €{formatCurrency(contributo.importo)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          {/* Totale Generale */}
          <div className="mt-8 bg-[#002060] rounded-xl p-6 text-center">
            <p className="text-white/80 mb-2">Totale generale versamenti</p>
            <p className="font-serif text-4xl font-bold text-[#FFD700]">
              €{formatCurrency(totaleGenerale)}
            </p>
          </div>
        </div>
      </section>

      {/* Elezioni Regionali */}
      <section className="py-16 bg-white" data-testid="trasparenza-regionali">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <span className="text-xs uppercase tracking-widest font-semibold text-[#FFD700] mb-2 block">
                Elezioni Regionali 2025
              </span>
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#002060]">
                Lista INSIEME - ENSEMBLE
              </h2>
              <p className="text-slate-600">
                Renaissance Valdôtaine - Forza Italia
              </p>
            </div>
            <Link to="/elezioni-regionali">
              <Button className="btn-primary px-5 py-2 rounded-lg text-sm flex items-center gap-2">
                Vedi tutti i candidati
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-[#F4F6F8] rounded-xl overflow-hidden">
              <thead>
                <tr className="bg-[#002060] text-white">
                  <th className="text-left py-4 px-4 font-semibold">#</th>
                  <th className="text-left py-4 px-4 font-semibold">Candidato</th>
                  <th className="text-center py-4 px-4 font-semibold">Curriculum Vitae</th>
                  <th className="text-center py-4 px-4 font-semibold">Certificato Penale</th>
                </tr>
              </thead>
              <tbody>
                {candidatiRegionali.map((candidato, index) => (
                  <tr 
                    key={`reg-${candidato.nome}`}
                    className="border-b border-slate-200 hover:bg-white transition-colors"
                  >
                    <td className="py-3 px-4 text-[#5CB9E5] font-semibold">
                      {String(index + 1).padStart(2, '0')}
                    </td>
                    <td className="py-3 px-4 font-medium text-[#002060]">
                      {candidato.nome}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <a 
                        href={candidato.cv}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#002060] hover:text-[#5CB9E5] transition-colors text-sm"
                      >
                        <FileText className="w-4 h-4" />
                        CV
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <a 
                        href={candidato.certificato}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#002060] hover:text-[#5CB9E5] transition-colors text-sm"
                      >
                        <FileText className="w-4 h-4" />
                        Certificato
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Elezioni Comunali */}
      <section className="py-16 bg-[#F4F6F8]" data-testid="trasparenza-comunali">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5CB9E5] mb-2 block">
                Elezioni Comunali Aosta 2025
              </span>
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#002060]">
                Lista LA RENAISSANCE VALDÔTAINE
              </h2>
            </div>
            <Link to="/elezioni-comunali">
              <Button className="btn-primary px-5 py-2 rounded-lg text-sm flex items-center gap-2">
                Vedi tutti i candidati
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          {/* Candidato Sindaco e Vice */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 border-l-4 border-[#FFD700]">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#FFD700] mb-1 block">
                Candidato Sindaco
              </span>
              <h3 className="font-serif text-xl font-bold text-[#002060] mb-3">
                {elezioni2025.comunali.candidatoSindaco.nome}
              </h3>
              <div className="flex gap-4 text-sm">
                <a 
                  href={elezioni2025.comunali.candidatoSindaco.cv}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[#002060] hover:text-[#5CB9E5] transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  CV
                </a>
                <a 
                  href={elezioni2025.comunali.candidatoSindaco.certificato}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[#002060] hover:text-[#5CB9E5] transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  Certificato
                </a>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border-l-4 border-[#5CB9E5]">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5CB9E5] mb-1 block">
                Candidato Vicesindaco
              </span>
              <h3 className="font-serif text-xl font-bold text-[#002060] mb-3">
                {elezioni2025.comunali.candidatoVice.nome}
              </h3>
              <div className="flex gap-4 text-sm">
                <a 
                  href={elezioni2025.comunali.candidatoVice.cv}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[#002060] hover:text-[#5CB9E5] transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  CV
                </a>
                <a 
                  href={elezioni2025.comunali.candidatoVice.certificato}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[#002060] hover:text-[#5CB9E5] transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  Certificato
                </a>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl overflow-hidden">
              <thead>
                <tr className="bg-[#002060] text-white">
                  <th className="text-left py-4 px-4 font-semibold">#</th>
                  <th className="text-left py-4 px-4 font-semibold">Candidato</th>
                  <th className="text-center py-4 px-4 font-semibold">Curriculum Vitae</th>
                  <th className="text-center py-4 px-4 font-semibold">Certificato Penale</th>
                </tr>
              </thead>
              <tbody>
                {candidatiComunali.map((candidato, index) => (
                  <tr 
                    key={`com-${candidato.nome}`}
                    className="border-b border-slate-100 hover:bg-[#F4F6F8] transition-colors"
                  >
                    <td className="py-3 px-4 text-[#5CB9E5] font-semibold">
                      {String(index + 1).padStart(2, '0')}
                    </td>
                    <td className="py-3 px-4 font-medium text-[#002060]">
                      {candidato.nome}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <a 
                        href={candidato.cv}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#002060] hover:text-[#5CB9E5] transition-colors text-sm"
                      >
                        <FileText className="w-4 h-4" />
                        CV
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <a 
                        href={candidato.certificato}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#002060] hover:text-[#5CB9E5] transition-colors text-sm"
                      >
                        <FileText className="w-4 h-4" />
                        Certificato
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};
